import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import cloudApi from '../../../services/cloudApi';
import StatusBanners from '../BillingView/components/StatusBanners';
import TeamSection from './TeamSection';
// LEGACY LIFETIME: see utils/legacyLifetime.js. Remove with the feature.
import { isLifetimePlan } from '../../../utils/legacyLifetime';

const formatDate = (value) =>
  value ? new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : null;

const formatMonthYear = (value) =>
  value ? new Date(value).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : null;

const getInitials = (name) => {
  if (!name) return '?';
  return name.split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2);
};

const AccountView = () => {
  const { effectiveUser: user, loading: authLoading, currentWorkspace, subscription: billingStatus, subscriptionLoading, refreshSubscription } = useAuth();
  const isWorkspaceMember = currentWorkspace?.role === 'member';
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({ name: '' });
  const [saving, setSaving] = useState(false);

  const [plans, setPlans] = useState([]);
  const [creditUsage, setCreditUsage] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [notification, setNotification] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const plansData = await cloudApi.getAvailablePlans();
      setPlans(plansData.plans || []);
    } catch (error) {
      console.error('Failed to load plans:', error);
    }
    try {
      const usageData = await cloudApi.getCreditUsage();
      setCreditUsage(usageData);
    } catch (error) {
      console.error('Failed to load credit usage:', error);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    const checkout = searchParams.get('checkout');
    if (checkout === 'success') {
      setNotification({ type: 'success', message: 'Subscription updated successfully!' });
      setTimeout(() => {
        refreshSubscription();
        loadData();
      }, 2000);
    } else if (checkout === 'cancel') {
      setNotification({ type: 'info', message: 'Checkout was cancelled.' });
    }
    if (checkout) {
      searchParams.delete('checkout');
      searchParams.delete('session_id');
      searchParams.delete('type');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, loadData, refreshSubscription]);

  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => setNotification(null), 8000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const loading = authLoading || subscriptionLoading;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-gray-400">No user data available</p>
      </div>
    );
  }

  const subscription = billingStatus?.subscription || {};
  const currentPlan = subscription.plan?.name || 'free';
  const planDisplayName = subscription.plan?.display_name || 'Free';
  const subscriptionStatus = subscription.status;
  const cancelAtPeriodEnd = subscription.cancel_at_period_end;
  const pendingChange = subscription.pending_plan_change;
  // Price comes from the subscription snapshot (plan_internal) — the locked-in
  // price this workspace actually pays, which survives later repricing. Fall
  // back to the public plans list for older rows that predate price snapshots.
  const planData = subscription.plan || plans.find((p) => p.name === currentPlan);
  const interval = subscription.plan_stripe?.interval;
  const isYearly = interval === 'year';
  // v2 plans are yearly-only with no monthly `price`; their yearly_price is the
  // annual total. Legacy plans store yearly_price as a per-month equivalent.
  const isAnnualOnly = isYearly && planData?.price == null;
  const price = isYearly ? planData?.yearly_price : planData?.price;
  // LEGACY LIFETIME: a one-time Chrome-extension purchase honoured here. Also
  // priceless, but it is not a custom plan and saying "managed by our team"
  // about something they bought outright reads as us having assigned it.
  const isLifetime = isLifetimePlan(currentPlan);
  // Enterprise / admin-set custom plans have no public price and usually no
  // Stripe billing — don't render a fabricated "$null/mo · renews" line.
  const isCustomPlan = currentPlan !== 'free' && !isLifetime
    && planData?.price == null && planData?.yearly_price == null;
  const nextBilling = subscription.current_period_end;
  const nextCreditReplenishment = subscription.next_credit_replenishment;
  const hasStripeCustomer = billingStatus?.has_stripe_customer;
  const isCancelling = cancelAtPeriodEnd && currentPlan !== 'free';
  const isFree = currentPlan === 'free';

  const credits = billingStatus?.credits || {};
  const available = credits.available || 0;
  const consumed = credits.consumed || 0;
  const total = credits.total || 0;
  const expiringSoon = credits.expiring_soon || 0;
  const usedPct = total > 0 ? Math.min((consumed / total) * 100, 100) : 0;

  const handleEdit = () => {
    setFormData({ name: user.name || '' });
    setEditing(true);
  };

  const handleSave = async () => {
    setSaving(true);
    setTimeout(() => {
      setSaving(false);
      setEditing(false);
    }, 1000);
  };

  const handleAvatarUpload = () => {
    console.log('Avatar upload not yet implemented');
  };

  const handleOpenPortal = async () => {
    setActionLoading('portal');
    try {
      const { url } = await cloudApi.createPortalSession();
      window.location.href = url;
    } catch (error) {
      setNotification({ type: 'error', message: error.message || 'Failed to open billing portal' });
      setActionLoading(null);
    }
  };

  const planStatusPill = (() => {
    if (isFree) return { label: 'Free', tone: 'neutral' };
    if (isCancelling) return { label: `Cancels ${formatDate(nextBilling)}`, tone: 'warning' };
    if (pendingChange) {
      return {
        label: `Changing to ${pendingChange.display_name} · ${formatDate(pendingChange.effective_date)}`,
        tone: 'info',
      };
    }
    if (subscriptionStatus === 'past_due') return { label: 'Payment failed', tone: 'danger' };
    if (isLifetime) return { label: 'Lifetime', tone: 'success' };
    return { label: 'Active', tone: 'success' };
  })();

  const toneClasses = {
    neutral: 'bg-gray-400/10 text-gray-500 dark:text-gray-400 ring-gray-400/20',
    success: 'bg-emerald-400/10 text-emerald-600 dark:text-emerald-400 ring-emerald-400/20',
    warning: 'bg-amber-400/10 text-amber-600 dark:text-amber-400 ring-amber-400/20',
    info: 'bg-blue-400/10 text-blue-600 dark:text-blue-400 ring-blue-400/20',
    danger: 'bg-red-400/10 text-red-600 dark:text-red-400 ring-red-400/20',
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {notification && (
        <div
          role="status"
          aria-live="polite"
          className={`rounded-xl p-4 border animate-fade-in ${
            notification.type === 'success'
              ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-800 dark:text-green-200'
              : notification.type === 'error'
              ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-800 dark:text-red-200'
              : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-200'
          }`}
        >
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium">{notification.message}</p>
            <button
              onClick={() => setNotification(null)}
              className="ml-4 text-sm opacity-60 hover:opacity-100"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {(subscriptionStatus === 'past_due' || subscriptionStatus === 'grace_period' || subscription.payment_processing) && (
        <StatusBanners
          subscriptionStatus={subscriptionStatus}
          cancelAtPeriodEnd={false}
          billingStatus={billingStatus}
          onOpenPortal={handleOpenPortal}
        />
      )}

      {/* Profile card */}
      <section className="rounded-2xl bg-white dark:bg-gray-900/40 ring-1 ring-gray-200 dark:ring-gray-800/70 shadow-sm overflow-hidden">
        {!editing ? (
          <div className="p-6 flex items-center gap-5">
            <ProfileAvatar user={user} size="lg" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className="text-lg font-semibold tracking-tight text-gray-900 dark:text-gray-50 truncate">
                  {user.name || 'Not set'}
                </p>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{user.email}</p>
              {user.createdAt && (
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                  Member since {formatMonthYear(user.createdAt)}
                </p>
              )}
            </div>
            <button
              onClick={handleEdit}
              className="shrink-0 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 rounded-lg ring-1 ring-gray-200 dark:ring-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors"
            >
              Edit
            </button>
          </div>
        ) : (
          <div className="p-6 space-y-5">
            <div className="flex items-center gap-5">
              <div className="relative shrink-0">
                <ProfileAvatar user={user} size="lg" />
                <label className="absolute -bottom-1 -right-1 bg-white dark:bg-gray-800 ring-1 ring-gray-200 dark:ring-gray-700 rounded-full p-1.5 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors shadow-sm">
                  <svg className="w-3 h-3 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <input type="file" accept="image/*" onChange={handleAvatarUpload} className="hidden" />
                </label>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Change photo</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5">Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg ring-1 ring-gray-200 dark:ring-gray-700 focus:ring-2 focus:ring-cyan-500/60 bg-white dark:bg-gray-800/50 text-gray-900 dark:text-gray-100 text-sm outline-none"
                />
              </div>
              <div>
                <label className="block text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5">Email</label>
                <input
                  type="email"
                  value={user.email || ''}
                  disabled
                  className="w-full px-3 py-2 rounded-lg ring-1 ring-gray-200 dark:ring-gray-700 bg-gray-50 dark:bg-gray-800/80 text-gray-500 dark:text-gray-400 text-sm cursor-not-allowed"
                />
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-5 py-2 text-sm font-medium bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-lg hover:from-blue-600 hover:to-cyan-600 disabled:opacity-50 transition-colors"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
              <button
                onClick={() => setEditing(false)}
                className="px-5 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 rounded-lg ring-1 ring-gray-200 dark:ring-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </section>

      {/* Subscription + credits hero */}
      {billingStatus && (
        <section className="rounded-2xl bg-white dark:bg-gray-900/40 ring-1 ring-gray-200 dark:ring-gray-800/70 shadow-sm overflow-hidden">
          {/* Plan header */}
          <div className="p-6 flex items-start justify-between gap-4 border-b border-gray-100 dark:border-gray-800/70">
            <div className="min-w-0">
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Current Plan
              </p>
              <div className="mt-1 flex items-center gap-3 flex-wrap">
                <h2 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-gray-50 capitalize">
                  {planDisplayName}
                </h2>
                <span
                  className={`text-xs font-medium px-2 py-0.5 rounded-full ring-1 ring-inset ${toneClasses[planStatusPill.tone]}`}
                >
                  {planStatusPill.label}
                </span>
              </div>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {isFree
                  ? 'No charge · upgrade anytime'
                  : isLifetime
                  ? 'Lifetime licence · one-time purchase, nothing to renew'
                  : isCustomPlan
                  ? 'Custom plan · managed by our team'
                  : isAnnualOnly
                  ? `$${price}/yr · billed yearly`
                  : isYearly
                  ? `$${price}/mo · billed yearly`
                  : `$${price}/mo · billed monthly`}
                {!isFree && !isCustomPlan && !isLifetime && nextBilling && !isCancelling && !pendingChange && (
                  <>
                    {' · '}
                    {isYearly ? 'Billing renews' : 'Renews'} {formatDate(nextBilling)}
                  </>
                )}
              </p>
            </div>

            <div className="shrink-0 flex items-center gap-3">
              {isWorkspaceMember ? (
                <span
                  className="text-xs text-gray-500 dark:text-gray-400 max-w-[200px] text-right"
                  title="Only the workspace owner can manage billing"
                >
                  Billing managed by workspace owner
                </span>
              ) : (
                <>
                  <button
                    onClick={() => navigate('/plans')}
                    className="hidden sm:inline text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                  >
                    View Plans
                  </button>
                  {hasStripeCustomer ? (
                    <button
                      onClick={handleOpenPortal}
                      disabled={actionLoading === 'portal'}
                      className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 shadow-sm transition-colors disabled:opacity-60"
                    >
                      {actionLoading === 'portal'
                        ? 'Opening...'
                        : isLifetime ? 'Billing & Invoices' : 'Manage Subscription'}
                    </button>
                  ) : (
                    <button
                      onClick={() => navigate('/plans')}
                      className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 shadow-sm transition-colors"
                    >
                      {isFree ? 'Upgrade Plan' : 'View Plans'}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Credits hero */}
          <div className="p-6">
            <div className="flex items-end justify-between gap-4 flex-wrap">
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Credits available
                </p>
                <p className="mt-1 text-4xl font-semibold tracking-tight text-gray-900 dark:text-gray-50 tabular-nums">
                  {available.toLocaleString()}
                  <span className="ml-2 text-base font-normal text-gray-400 dark:text-gray-500 tabular-nums">
                    / {total.toLocaleString()}
                  </span>
                </p>
              </div>
              <div className="flex items-center gap-4">
                {nextCreditReplenishment && (
                  <div className="text-right">
                    <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Next replenishment
                    </p>
                    <p className="mt-1 text-sm font-medium text-emerald-600 dark:text-emerald-400">
                      {formatDate(nextCreditReplenishment)}
                    </p>
                  </div>
                )}
                {!isFree && !isWorkspaceMember && (
                  <button
                    onClick={() => navigate('/topups')}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-blue-500 via-cyan-500 to-purple-500 hover:from-blue-600 hover:via-cyan-600 hover:to-purple-600 shadow-md shadow-cyan-500/20 hover:shadow-lg hover:shadow-cyan-500/30 hover:scale-[1.02] transition-all duration-200"
                  >
                    <span className="text-base font-bold leading-none">+</span>
                    Buy Credits
                  </button>
                )}
              </div>
            </div>

            {/* Progress bar */}
            <div className="mt-4 h-2 w-full rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
              <div
                className="h-full rounded-full bg-gradient-to-r from-blue-400 via-cyan-400 to-purple-400 transition-all duration-700"
                style={{ width: `${100 - usedPct}%` }}
              />
            </div>

            {/* Secondary stats */}
            <dl className="mt-5 grid grid-cols-3 gap-4 text-sm">
              <Stat label="Consumed" value={consumed.toLocaleString()} />
              <Stat label="Total allocated" value={total.toLocaleString()} />
              <Stat
                label="Expiring soon"
                value={expiringSoon.toLocaleString()}
                tone={expiringSoon > 0 ? 'warning' : 'neutral'}
              />
            </dl>
          </div>

          {/* Usage chart or empty state */}
          <UsageSection usage={creditUsage?.usage} />
        </section>
      )}

      {currentWorkspace && (
        <TeamSection workspace={currentWorkspace} onNotify={setNotification} />
      )}
    </div>
  );
};

const ProfileAvatar = ({ user, size = 'lg' }) => {
  const dims = size === 'lg' ? 'w-16 h-16 text-lg' : 'w-10 h-10 text-sm';
  if (user.avatarUrl) {
    return (
      <img
        src={user.avatarUrl}
        alt={user.name || 'Profile'}
        referrerPolicy="no-referrer"
        className={`${dims} rounded-full object-cover ring-2 ring-white dark:ring-gray-800 shadow-sm`}
      />
    );
  }
  return (
    <div
      className={`${dims} rounded-full ring-2 ring-white dark:ring-gray-800 shadow-sm bg-gradient-to-br from-blue-400 via-cyan-400 to-purple-400 flex items-center justify-center`}
    >
      <span className="font-bold text-white">{getInitials(user.name)}</span>
    </div>
  );
};

const Stat = ({ label, value, tone = 'neutral' }) => {
  const color = tone === 'warning' ? 'text-amber-600 dark:text-amber-400' : 'text-gray-900 dark:text-gray-100';
  return (
    <div>
      <dt className="text-xs text-gray-500 dark:text-gray-400">{label}</dt>
      <dd className={`mt-0.5 text-base font-semibold tabular-nums ${color}`}>{value}</dd>
    </div>
  );
};

const UsageSection = ({ usage }) => {
  const [tooltip, setTooltip] = useState(null);

  const { hasAnyUsage, maxConsumed, labelIndices, totalConsumed } = useMemo(() => {
    if (!usage || usage.length === 0) {
      return { hasAnyUsage: false, maxConsumed: 0, labelIndices: new Set(), totalConsumed: 0 };
    }
    const indices = new Set([0, usage.length - 1]);
    for (let i = 7; i < usage.length - 3; i += 7) indices.add(i);
    return {
      hasAnyUsage: usage.some((d) => d.consumed > 0),
      maxConsumed: Math.max(...usage.map((d) => d.consumed), 100),
      labelIndices: indices,
      totalConsumed: usage.reduce((sum, d) => sum + (d.consumed || 0), 0),
    };
  }, [usage]);

  if (!usage || usage.length === 0) return null;

  const formatBarDate = (dateStr) => {
    const d = new Date(dateStr + 'T00:00:00Z');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
  };

  const barAreaHeight = 72;

  if (!hasAnyUsage) {
    return (
      <div className="px-6 pb-6">
        <div className="flex items-center justify-between rounded-xl bg-gray-50 dark:bg-gray-800/40 px-4 py-3">
          <div>
            <p className="text-sm font-medium text-gray-700 dark:text-gray-200">Daily credit usage</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">Last 30 days</p>
          </div>
          <p className="text-sm text-gray-400 dark:text-gray-500">No usage yet</p>
        </div>
      </div>
    );
  }

  return (
    <div className="px-6 pb-6">
      <div className="rounded-xl bg-gray-50 dark:bg-gray-800/40 p-4">
        <div className="flex items-end justify-between mb-4">
          <div>
            <p className="text-sm font-medium text-gray-700 dark:text-gray-200">Daily credit usage</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">Last 30 days</p>
          </div>
          <div className="text-right">
            <p className="text-xs text-gray-500 dark:text-gray-400">Total</p>
            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 tabular-nums">
              {totalConsumed.toLocaleString()}
            </p>
          </div>
        </div>

        <div className="relative">
          {tooltip && (
            <div
              className="absolute z-10 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-gray-900 dark:bg-gray-700 text-white shadow-lg pointer-events-none whitespace-nowrap"
              style={{ left: tooltip.x, top: -8, transform: 'translate(-50%, -100%)' }}
            >
              <div>{formatBarDate(tooltip.date)}</div>
              <div className="text-cyan-300">{tooltip.consumed.toLocaleString()} credits</div>
            </div>
          )}

          <div className="flex items-end gap-px" style={{ height: barAreaHeight }}>
            {usage.map((d) => {
              const heightPct = d.consumed / maxConsumed;
              const barHeight = Math.max(heightPct * barAreaHeight, d.consumed > 0 ? 3 : 0);
              return (
                <div
                  key={d.date}
                  className="relative flex-1 group cursor-default"
                  style={{ height: barAreaHeight }}
                  onMouseEnter={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    const containerRect = e.currentTarget.closest('.relative').getBoundingClientRect();
                    setTooltip({
                      date: d.date,
                      consumed: d.consumed,
                      x: rect.left - containerRect.left + rect.width / 2,
                    });
                  }}
                  onMouseLeave={() => setTooltip(null)}
                >
                  <div
                    className="absolute bottom-0 left-0 right-0 rounded-t-sm transition-opacity group-hover:opacity-80"
                    style={{
                      height: barHeight,
                      background:
                        d.consumed > 0 ? 'linear-gradient(to top, #22d3ee, #60a5fa)' : undefined,
                    }}
                  >
                    {d.consumed === 0 && (
                      <div className="absolute bottom-0 left-0 right-0 h-px bg-gray-200 dark:bg-gray-700" />
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex mt-1.5">
            {usage.map((d, i) => (
              <div key={d.date} className="flex-1 relative">
                {labelIndices.has(i) && (
                  <span className="absolute left-1/2 -translate-x-1/2 text-[10px] text-gray-400 dark:text-gray-500 whitespace-nowrap">
                    {formatBarDate(d.date)}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountView;
